import React, { useState } from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import { MainCard } from '~/components/core/NewDesignSystem/Cards/MainCard/MainCard';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import useOrganization from '~/components/OrganizationContext';
import type {
  ClaimMilestoneDefinitionCreateJson,
  ClaimMilestoneDefinitionModel,
} from '~/server_shared/openapi/data-contracts';

import ClaimMilestoneAddEditDialog from './components/ClaimMilestoneAddEditDialog';
import ClaimMilestoneConfigurationEmptyState from './components/ClaimMilestoneConfigurationEmptyState';
import ClaimMilestoneRow, { milestoneRowGrid } from './components/ClaimMilestoneRow';
import useClaimMilestoneDefinitionsAndMutations from './hooks/useClaimMilestoneDefinitionsAndMutations';
import TEXTS from './texts';

const ClaimMilestoneConfigurationHeader: React.FC = () => {
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled?: boolean };

  return (
    <div className={`${milestoneRowGrid(!!subOrganizationEnabled)} border-b border-slate-300 px-8 py-8`}>
      <span />
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.name}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.key}
      </Text>
      {subOrganizationEnabled ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.columns.subOrgs}
        </Text>
      ) : null}
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.lobs}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.columns.conditional}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="justify-self-end">
        {TEXTS.columns.active}
      </Text>
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="justify-self-end">
        {TEXTS.columns.actions}
      </Text>
    </div>
  );
};

const ClaimMilestoneConfiguration: React.FC = () => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    editedMilestone: ClaimMilestoneDefinitionModel | null;
  }>({ isOpen: false, editedMilestone: null });
  const { milestoneDefinitions, isLoading, isError, isUpdating, create, update, reorder } =
    useClaimMilestoneDefinitionsAndMutations();

  // Local order so a drop reorders instantly; resynced to the server whenever the fetched list changes, and
  // every write refetches it, which is what reverts a failed reorder. The resync happens during render (not
  // in an effect) so the new order shows in the same paint — otherwise the empty-state/list gate below would
  // read a stale empty list for one post-load paint and briefly flash the empty state.
  const [orderedMilestones, setOrderedMilestones] = useState(milestoneDefinitions);
  const [syncedDefinitions, setSyncedDefinitions] = useState(milestoneDefinitions);
  if (syncedDefinitions !== milestoneDefinitions) {
    setSyncedDefinitions(milestoneDefinitions);
    setOrderedMilestones(milestoneDefinitions);
  }

  const closeDialog = () => setDialogState({ isOpen: false, editedMilestone: null });
  const handleCreateClick = () => setDialogState({ isOpen: true, editedMilestone: null });
  const handleEditClick = (milestone: ClaimMilestoneDefinitionModel) =>
    setDialogState({ isOpen: true, editedMilestone: milestone });

  const handleSubmit = async (values: ClaimMilestoneDefinitionCreateJson) => {
    try {
      if (dialogState.editedMilestone) {
        // The key is immutable and absent from the update schema, so the PATCH sends only the mutable fields.
        const { key: _key, ...updateValues } = values;
        await update(dialogState.editedMilestone.key, updateValues);
      } else {
        await create(values);
      }
      closeDialog();
    } catch {
      // The mutation already surfaced the error; keep the dialog open so the admin's input isn't lost.
    }
  };

  // The Switch and the list fire these without awaiting them. runMutation already reports a failure and
  // resyncs the list, so the rethrow is swallowed here rather than left as an unhandled rejection.
  const handleToggle = (milestone: ClaimMilestoneDefinitionModel) =>
    update(milestone.key, { is_enabled: !milestone.is_enabled }).catch(() => {});

  const handleReorder = ({ fromIndex, toIndex }: DndOrderedListReorder) => {
    const reordered = [...orderedMilestones];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setOrderedMilestones(reordered);
    reorder(reordered.map((milestone) => milestone.key)).catch(() => {});
  };

  return (
    <MainCard type="elevated" title={TEXTS.sectionTitle}>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="-mt-4">
        {TEXTS.sectionDescription}
      </Text>
      {isLoading || isError ? (
        <SkeletonTable rowsCount={6} columnsCount={1} isError={isError} />
      ) : orderedMilestones.length === 0 ? (
        <ClaimMilestoneConfigurationEmptyState onCreateClick={handleCreateClick} />
      ) : (
        <>
          <Button color="primary" onClick={handleCreateClick} className="self-end" disabled={isUpdating}>
            <AddIcon />
            {TEXTS.addMilestone}
          </Button>
          <div>
            <ClaimMilestoneConfigurationHeader />
            <DndOrderedList
              className="flex flex-col gap-8"
              disabled={isUpdating}
              items={orderedMilestones}
              getItemId={(milestone) => milestone.key}
              renderItem={(milestone, { dragHandleProps }) => (
                <ClaimMilestoneRow
                  milestone={milestone}
                  dragHandleProps={dragHandleProps}
                  onToggle={() => handleToggle(milestone)}
                  onEdit={() => handleEditClick(milestone)}
                  disabled={isUpdating}
                />
              )}
              onReorder={handleReorder}
            />
          </div>
        </>
      )}

      <ClaimMilestoneAddEditDialog
        isOpen={dialogState.isOpen}
        editedMilestone={dialogState.editedMilestone}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        existingKeys={orderedMilestones.map((milestone) => milestone.key)}
      />
    </MainCard>
  );
};

export default ClaimMilestoneConfiguration;
